import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiFetch from "../../utils/apiFetch";
import { authHeaders } from "./helpers";
import type { PayStub, PayStubRun } from "./types";

export interface PayStubDetailData {
  run: PayStubRun | null;
  stub: PayStub;
}

export function usePayStubDetail(
  runId: string | undefined,
  stubId: string | undefined
) {
  return useQuery<PayStubDetailData>({
    queryKey: ["pay-stub-detail", runId, stubId],
    queryFn: async () => {
      if (!runId || !stubId) throw new Error("Missing run or stub id");

      let runData: PayStubRun | null = null;
      const runRes = await apiFetch("/api/v1/payroll/runs/" + runId, {
        headers: authHeaders(),
      });
      if (runRes.status === 404 || runRes.status === 405) {
        const listRes = await apiFetch("/api/v1/payroll/runs", {
          headers: authHeaders(),
        });
        if (listRes.ok) {
          const list = await listRes.json();
          runData =
            (Array.isArray(list) ? list : []).find(
              (r: PayStubRun) => String(r.id) === String(runId)
            ) || null;
        }
      } else if (runRes.ok) {
        runData = await runRes.json();
      }

      const stubsRes = await apiFetch(
        "/api/v1/payroll/runs/" + runId + "/stubs",
        { headers: authHeaders() }
      );
      if (!stubsRes.ok) {
        throw new Error(
          "Could not load pay stubs (HTTP " + stubsRes.status + ")"
        );
      }
      const stubsData = await stubsRes.json();
      const list: PayStub[] = Array.isArray(stubsData)
        ? stubsData
        : stubsData.stubs || stubsData.data || [];
      const found = list.find((s) => String(s.id) === String(stubId));
      if (!found) throw new Error("Pay stub not found");

      return { run: runData, stub: found };
    },
    enabled: !!(runId && stubId),
    refetchOnWindowFocus: false,
  });
}

export function useInvalidatePayStubDetail(
  runId: string | undefined,
  stubId: string | undefined
) {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({ queryKey: ["pay-stub-detail", runId, stubId] });
}